import * as THREE from 'three';
import { Capsule } from './Collider.js';
import { makeCollisionResult } from './PhysicsWorld.js';

const _wish = new THREE.Vector3();
const _flat = new THREE.Vector3();
const _delta = new THREE.Vector3();
const _tmp = new THREE.Vector3();
const _snap = new THREE.Vector3();
const _snapStart = new THREE.Vector3();
const _snapEnd = new THREE.Vector3();
const _basis = new THREE.Matrix3();

/**
 * How far below the feet to look for floor before declaring the body airborne.
 *
 * Depenetration deliberately pushes out slightly further than the overlap, so
 * a body at rest ends the frame a fraction of a millimetre above the floor.
 * Next frame there is no contact at all, so a naive test reports airborne,
 * gravity pulls it back, and it lands again. Measured, that toggled `grounded`
 * on every single frame while standing perfectly still: a landing event (and
 * its sound) eight times a second, and footsteps that never fired because the
 * airborne branch kept resetting their accumulator.
 *
 * Small enough not to cling to a ledge you walked off, large enough to swallow
 * the push-out and to keep contact walking down shallow slopes.
 */
const GROUND_SNAP = 0.08;

/** Below this, a contact is a re-seat rather than a landing worth hearing. */
const LAND_MIN_IMPACT = 1.2;
const LAND_MIN_AIRTIME = 0.09;

/**
 * First person movement with Quake-flavoured acceleration.
 *
 * The single most important property for this game is that nothing silently
 * eats speed: there is no air friction and no velocity clamp, so a 200 metre
 * drop turns into a 60 m/s horizontal launch when it exits a wall portal.
 * Ground friction is the only thing that bleeds momentum, and jumping before
 * you land skips it entirely.
 */
export class CharacterController {
  constructor(world, opts = {}) {
    this.world = world;

    this.height = opts.height ?? 1.8;
    this.radius = opts.radius ?? 0.34;
    this.eyeHeight = opts.eyeHeight ?? 1.62;

    this.walkSpeed = opts.walkSpeed ?? 7.6;
    this.sprintSpeed = opts.sprintSpeed ?? 11.4;
    this.groundAccel = opts.groundAccel ?? 11;
    this.airAccel = opts.airAccel ?? 55;
    this.airWishCap = opts.airWishCap ?? 1.5;   // how hard you may steer mid flight
    // Above the cap, steering ROTATES the horizontal velocity instead of
    // adding to it: constant angular authority at any speed, speed itself
    // conserved (the game's promise), and no dead cone. Probed 2026-08-18:
    // the old add-only steer gave 47deg/s at 6 m/s but 9deg TOTAL at 40,
    // ignored any input within ~75deg of the velocity (forward+left did
    // exactly nothing at 25 m/s), and pulled-back input shed 18 m/s in
    // 0.3s. Those three measurements are the whole "air control feels
    // inconsistent" complaint.
    this.airTurnRate = opts.airTurnRate ?? 1.05; // rad/s at full deflection (~60deg/s)
    this.airBrake = opts.airBrake ?? 10;         // m/s^2 against opposed input
    // The two regimes BLEND across this speed band instead of switching at
    // the cap: measured, the hard switch was a 59deg-vs-23deg authority
    // cliff between 6.4 and 6.6 m/s, felt as a rigid wall.
    this.airBlendLo = opts.airBlendLo ?? 4.5;
    this.airBlendHi = opts.airBlendHi ?? 9.0;
    // Turns ease toward the commanded rate (a bank, not a switch): the
    // paper-plane feel, and releasing input leaves a decaying arc instead
    // of a hard stop. Measured before easing: heading-rate jitter of
    // +/-511 deg/s while steering through hops.
    this.airTurnEase = opts.airTurnEase ?? 0.18; // seconds to reach the rate
    this._turnVel = 0;
    this.friction = opts.friction ?? 8.5;
    this.stopSpeed = opts.stopSpeed ?? 2.4;
    this.jumpVelocity = opts.jumpVelocity ?? 9.4;
    this.gravity = opts.gravity ?? -26;
    this.terminalVelocity = opts.terminalVelocity ?? 140;
    this.coyoteTime = opts.coyoteTime ?? 0.13;
    this.jumpBuffer = opts.jumpBuffer ?? 0.16;
    this.autoHop = opts.autoHop ?? true;
    // Read and cleared by the trigger sweep: a teleport must never be
    // interpolated as if the body crossed the space in between.
    this.justTeleported = false;

    // Gravity fields hold a scale for a short refreshed window rather than
    // toggling on enter/exit: trigger edges are unreliable when two bodies
    // share one trigger's inside flag, a refreshed decay is not.
    this._gravScale = 1;
    this._gravHold = 0;

    // Surface slip, same refreshed-hold discipline: ice and boost strips
    // scale ground friction (and control) only while their trigger keeps
    // refreshing the hold, so leaving the patch restores grip in a step.
    this._slipFriction = 1;
    this._slipAccel = 1;
    this._slipHold = 0;

    // System shock: the ion-tech debuff. For its duration, target move
    // speed halves and jump velocity drops to sqrt(1/2) (= HALF the jump
    // height, since height goes with v squared). A timed effect, not a
    // refreshed hold: one hit hurts for a fixed, feelable window.
    this._shockT = 0;
    this.shockSpeedScale = 0.5;
    this.shockJumpScale = Math.SQRT1_2;

    this.position = new THREE.Vector3();   // feet
    this.velocity = new THREE.Vector3();
    this.capsule = new Capsule();
    this.collision = makeCollisionResult();
    this._snapCollision = makeCollisionResult();
    this._airborneTime = 0;

    this.grounded = false;
    this.wasGrounded = false;
    this.timeSinceGrounded = 999;
    this.timeSinceJumpPress = 999;
    this.groundNormal = new THREE.Vector3(0, 1, 0);
    this.platformVelocity = new THREE.Vector3();

    this.enabled = true;
    this.frozen = false;

    // Run statistics, consumed by the HUD and the results screen.
    this.stats = { topSpeed: 0, maxDrop: 0, airTime: 0, portalTravels: 0 };
    this._fallStartY = 0;
    this._falling = false;

    /** (subDt) => boolean. Return true if the body was teleported this substep. */
    this.onSubstep = null;

    this.events = {
      onLand: null,      // (impactSpeed, normal)
      onJump: null,
      onStep: null,      // (distanceTravelled)
    };
    this._stepAccum = 0;
  }

  /** Ion tech (beams, sentinels) lands its debuff here. */
  applyShock(duration) {
    this._shockT = Math.max(this._shockT, duration);
  }

  /** Called every frame a body stands on a slick surface (ice, boost). */
  applySlip(frictionScale, accelScale = 1) {
    this._slipFriction = frictionScale;
    this._slipAccel = accelScale;
    this._slipHold = 0.15;
  }

  /** Called every frame a body stands in a gravity field. */
  setGravityScale(scale, hold = 0.15) {
    this._gravScale = scale;
    this._gravHold = hold;
  }

  teleportTo(x, y, z) {
    // A hard reposition is a fresh start: banked turn residue from before
    // the teleport must not arc the first airborne moments after it.
    this._turnVel = 0;
    this.justTeleported = true;
    this.position.set(x, y, z);
    this.velocity.set(0, 0, 0);
    this.grounded = false;
    this._falling = false;
    this._syncCapsule();
  }

  _syncCapsule() {
    this.capsule.setFromFeet(this.position, this.height, this.radius);
  }

  _syncFromCapsule() {
    this.position.set(this.capsule.start.x, this.capsule.start.y - this.radius, this.capsule.start.z);
  }

  /** Writes into `out` rather than returning a shared scratch vector. */
  getEyePosition(out) {
    return out.set(this.position.x, this.position.y + this.eyeHeight, this.position.z);
  }

  get horizontalSpeed() {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  get speed() { return this.velocity.length(); }

  /**
   * Rotate and move the whole state by a portal transform. Velocity has to be
   * rotated by the same basis or the exit direction is wrong, which is the
   * classic "portal eats my speed" bug.
   */
  applyTransform(matrix) {
    this.position.applyMatrix4(matrix);
    // The portal transform is rigid, so its upper 3x3 is orthonormal and
    // rotating the velocity through it preserves speed exactly. Renormalising
    // afterwards is not just redundant, it is wrong: reading the length back
    // off the vector you already normalised gives 1.
    _basis.setFromMatrix4(matrix);
    this.velocity.applyMatrix3(_basis);
    this._syncCapsule();
    this.grounded = false;
    this.timeSinceGrounded = this.coyoteTime + 0.01;
    this.stats.portalTravels++;
    // A drop that continues through a portal keeps accumulating, but a sideways
    // exit ends the fall, so restart the measurement from here.
    this._fallStartY = this.position.y;
  }

  /**
   * @param {number} dt
   * @param {{moveX:number, moveZ:number, yaw:number, jump:boolean, jumpPressed:boolean, sprint:boolean}} input
   */
  update(dt, input) {
    if (!this.enabled || dt <= 0) return;
    if (this.frozen) { this._syncCapsule(); return; }

    this.wasGrounded = this.grounded;
    if (input.jumpPressed) this.timeSinceJumpPress = 0;
    else this.timeSinceJumpPress += dt;

    // ---- desired direction in world space, flattened onto the ground plane
    const sin = Math.sin(input.yaw);
    const cos = Math.cos(input.yaw);
    // yaw 0 looks down -Z, matching the camera's default orientation.
    _wish.set(
      input.moveX * cos - input.moveZ * sin,
      0,
      -input.moveX * sin - input.moveZ * cos,
    );
    const wishLen = _wish.length();
    if (wishLen > 1e-4) _wish.multiplyScalar(1 / wishLen);
    this._shockT = Math.max(0, this._shockT - dt);
    const shocked = this._shockT > 0;
    const wishSpeed = Math.min(wishLen, 1)
      * (input.sprint ? this.sprintSpeed : this.walkSpeed)
      * (shocked ? this.shockSpeedScale : 1);

    // ---- acceleration
    this._slipHold = Math.max(0, this._slipHold - dt);
    if (this.grounded) {
      this._applyFriction(dt);
      const accelScale = this._slipHold > 0 ? this._slipAccel : 1;
      this._accelerate(_wish, wishSpeed, this.groundAccel * accelScale, dt);
      // Any residual air turn banks out instead of surviving to the next
      // jump; the same easing that ramps it in bleeds it away.
      this._turnVel += (0 - this._turnVel) * Math.min(1, dt / this.airTurnEase);
    } else {
      this._airSteer(_wish, Math.min(wishLen, 1), dt);
    }

    // ---- jump (buffered + coyote so it never feels like it dropped an input)
    const canJump = this.grounded || this.timeSinceGrounded < this.coyoteTime;
    const wantsJump = this.timeSinceJumpPress < this.jumpBuffer || (this.autoHop && input.jump);
    if (canJump && wantsJump) {
      this.velocity.y = this.jumpVelocity * (shocked ? this.shockJumpScale : 1);
      // Riding platform speed carries into the jump.
      this.velocity.add(this.platformVelocity);
      this.grounded = false;
      this.timeSinceGrounded = this.coyoteTime + 1;
      this.timeSinceJumpPress = this.jumpBuffer + 1;
      this.events.onJump?.();
    }

    // ---- gravity
    this._gravHold = Math.max(0, this._gravHold - dt);
    const gscale = this._gravHold > 0 ? this._gravScale : 1;
    if (!this.grounded) {
      this.velocity.y += this.gravity * gscale * dt;
      if (this.velocity.y < -this.terminalVelocity) this.velocity.y = -this.terminalVelocity;
      this.stats.airTime += dt;
    } else if (this.velocity.y < 0) {
      this.velocity.y = 0;
    }

    // ---- integrate with substeps sized to the capsule so nothing tunnels
    _delta.copy(this.velocity).multiplyScalar(dt);
    if (this.collision.groundCollider?.isKinematic) {
      _delta.addScaledVector(this.collision.groundCollider.velocity, dt);
    }
    const travel = _delta.length();
    const maxStep = this.radius * 0.75;
    const steps = Math.min(24, Math.max(1, Math.ceil(travel / maxStep)));
    const inv = 1 / steps;
    _delta.multiplyScalar(inv);

    this._syncCapsule();
    let grounded = false;
    let groundNormal = null;
    let impact = 0;

    const subDt = dt * inv;
    for (let i = 0; i < steps; i++) {
      this.capsule.translate(_delta);

      // Portal traversal has to be tested here, between the move and the
      // collision response. Resolve first and a body that drops into a floor
      // portal lands on the bottom of the tunnel, has its velocity zeroed as
      // ground contact, and then teleports out the far side at a standstill:
      // the drop is spent and nothing comes back.
      this._syncFromCapsule();
      if (this.onSubstep && this.onSubstep(subDt)) {
        _delta.copy(this.velocity).multiplyScalar(subDt);
        this._syncCapsule();
        continue;
      }

      this.world.resolveCapsule(this.capsule, this.collision, { velocity: this.velocity });

      if (this.collision.contactCount > 0) {
        this._syncFromCapsule();
        if (this.collision.grounded) {
          if (!grounded) impact = Math.max(impact, -this.velocity.y);
          grounded = true;
          groundNormal = this.collision.groundNormal;
          if (this.velocity.y < 0) {
            this.velocity.y = 0;
            _delta.y = 0;
          }
        }
        if (this.collision.ceiling && this.velocity.y > 0) {
          this.velocity.y = 0;
          _delta.y = 0;
        }
        if (this.collision.wall) {
          // Slide: remove only the component pushing into the surface.
          const n = this.collision.wallNormal;
          const into = this.velocity.dot(n);
          if (into < 0) {
            this.velocity.addScaledVector(n, -into);
            _delta.copy(this.velocity).multiplyScalar(dt * inv);
          }
        }
      }
    }
    this._syncFromCapsule();

    // ---- re-seat onto floor we are a hair above rather than reporting a fall
    if (!grounded && this.wasGrounded && this.velocity.y <= 0.01) {
      _snapStart.copy(this.capsule.start);
      this.capsule.translate(_snap.set(0, -GROUND_SNAP, 0));
      this.world.resolveCapsule(this.capsule, this._snapCollision);
      if (this._snapCollision.grounded) {
        grounded = true;
        groundNormal = this._snapCollision.groundNormal;
        this.collision.groundCollider = this._snapCollision.groundCollider;
        this._syncFromCapsule();
      } else {
        // Put it back exactly: the probe's own depenetration may have shifted
        // it sideways out of a wall on the way down.
        _snapEnd.copy(this.capsule.start);
        this.capsule.translate(_snap.subVectors(_snapStart, _snapEnd));
        this._syncFromCapsule();
      }
    }

    // ---- ground state transitions
    this.grounded = grounded;
    if (grounded) {
      this.timeSinceGrounded = 0;
      if (groundNormal) this.groundNormal.copy(groundNormal);
      const gc = this.collision.groundCollider;
      this.platformVelocity.copy(gc?.isKinematic ? gc.velocity : _tmp.set(0, 0, 0));
    } else {
      this.timeSinceGrounded += dt;
      this.platformVelocity.set(0, 0, 0);
    }

    // Read the air time before clearing it: the landing test below needs how
    // long we were off the ground, not the zero it is about to become.
    const airTime = this._airborneTime;
    this._airborneTime = this.grounded ? 0 : this._airborneTime + dt;

    if (this.grounded && !this.wasGrounded) {
      const drop = Math.max(0, this._fallStartY - this.position.y);
      if (drop > this.stats.maxDrop) this.stats.maxDrop = drop;
      this._falling = false;
      // Second line of defence behind the snap: a contact regained within a
      // frame or two at walking speed is not a landing anyone should hear.
      if (impact > LAND_MIN_IMPACT || airTime > LAND_MIN_AIRTIME) {
        this.events.onLand?.(impact, this.groundNormal);
      }
    }
    if (!this.grounded && !this._falling && this.velocity.y < -0.5) {
      this._falling = true;
      this._fallStartY = this.position.y;
    }
    if (this.grounded) this._fallStartY = this.position.y;

    // ---- footstep cadence
    if (this.grounded) {
      this._stepAccum += this.horizontalSpeed * dt;
      if (this._stepAccum > 2.4) { this._stepAccum = 0; this.events.onStep?.(this.horizontalSpeed); }
    } else {
      this._stepAccum = 1.6;   // land already primed for the next step
    }

    const s = this.speed;
    if (s > this.stats.topSpeed) this.stats.topSpeed = s;
  }

  _applyFriction(dt) {
    _flat.set(this.velocity.x, 0, this.velocity.z);
    const speed = _flat.length();
    if (speed < 1e-4) { this.velocity.x = 0; this.velocity.z = 0; return; }
    const control = Math.max(speed, this.stopSpeed);
    const fric = this._slipHold > 0 ? this.friction * this._slipFriction : this.friction;
    let newSpeed = speed - control * fric * dt;
    if (newSpeed < 0) newSpeed = 0;
    const scale = newSpeed / speed;
    this.velocity.x *= scale;
    this.velocity.z *= scale;
  }

  /**
   * Mid-air control in two regimes. At or below the cap, the classic
   * accelerate: near-instant, total authority (the "effortless" feel of
   * slow drifts, and the regime every walker and drift law was tuned in).
   * Above the cap, input STEERS rather than pushes: the sideways part of
   * the wish rotates the horizontal velocity at a constant angular rate
   * with the speed conserved, and the opposed part brakes at a bounded
   * rate instead of a wall. Air steer is its own budget, not a slice of
   * walk speed: mid-air the walk/sprint distinction is meaningless, and
   * touch players (no sprint key) must still reach the full cap.
   */
  _airSteer(wishDir, deflection, dt) {
    const vx = this.velocity.x;
    const vz = this.velocity.z;
    const hSpeed = Math.hypot(vx, vz);

    // Rotation weight rises smoothly across the blend band; the additive
    // regime fades out over the same band. No cliff, and the hop cycle
    // (walk speed sits mid-band) keeps one continuous character.
    const t = (hSpeed - this.airBlendLo) / (this.airBlendHi - this.airBlendLo);
    const w = t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t);

    // Shock halves the air budget too, or bunny-hopping would sail
    // through the debuff at nearly full speed (air wish is its own budget,
    // never scaled by walk speed).
    const cap = this._shockT > 0 ? this.airWishCap * this.shockSpeedScale : this.airWishCap;
    if (deflection > 1e-4 && w < 1) {
      this._accelerate(wishDir, deflection * cap, this.airAccel * (1 - w), dt);
    }

    // Commanded turn from the sideways part of the wish; eased so a tap
    // banks in and a release glides out on a decaying arc. A zero cap means
    // LOCKED: the banking regime honors airWishCap = 0 (the LOCKED-IN
    // mutator's contract) instead of steering past it, which it wrongly did
    // from 1.4.0 through 1.11.4.
    let commanded = 0;
    if (deflection > 1e-4 && hSpeed > 1e-3 && this.airWishCap > 0) {
      const dirX = vx / hSpeed;
      const dirZ = vz / hSpeed;
      const side = wishDir.x * -dirZ + wishDir.z * dirX;        // signed lateral
      commanded = this.airTurnRate * side * deflection * w;

      // Opposed wish: a bounded trim, never the old 350 m/s^2 wall.
      // Braking stops at the cap, where the additive regime has taken
      // over and full redirection is available again.
      const along = wishDir.x * dirX + wishDir.z * dirZ;
      if (along < 0) {
        const drop = Math.min(
          this.airBrake * -along * deflection * w * dt,
          Math.max(0, hSpeed - this.airWishCap),
        );
        if (drop > 0) {
          const k = (hSpeed - drop) / hSpeed;
          this.velocity.x *= k;
          this.velocity.z *= k;
        }
      }
    }
    this._turnVel += (commanded - this._turnVel) * Math.min(1, dt / this.airTurnEase);

    const theta = this._turnVel * dt;
    if (Math.abs(theta) > 1e-6) {
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);
      const nvx = this.velocity.x;
      const nvz = this.velocity.z;
      this.velocity.x = nvx * cos - nvz * sin;
      this.velocity.z = nvx * sin + nvz * cos;
    }
  }

  _accelerate(wishDir, wishSpeed, accel, dt) {
    if (wishSpeed <= 0) return;
    const current = this.velocity.dot(wishDir);
    const add = wishSpeed - current;
    if (add <= 0) return;
    let accelSpeed = accel * wishSpeed * dt;
    if (accelSpeed > add) accelSpeed = add;
    this.velocity.addScaledVector(wishDir, accelSpeed);
  }

  /**
   * Launch pads REPLACE velocity rather than add to it. Adding makes the
   * landing point depend on how fast you happened to arrive, which turns a
   * timed course into a coin flip. Momentum chaining is what portals are for.
   */
  setLaunch(v) {
    this.velocity.copy(v);
    this.grounded = false;
    this.timeSinceGrounded = this.coyoteTime + 1;
    this._falling = false;
    this._fallStartY = this.position.y;
  }

  /** Impulse used by boosters that should stack with what you already have. */
  addImpulse(v, replaceVertical = false) {
    if (replaceVertical) this.velocity.y = 0;
    this.velocity.add(v);
    this.grounded = false;
    this.timeSinceGrounded = this.coyoteTime + 1;
    this._falling = false;
    this._fallStartY = this.position.y;
  }

  resetStats() {
    this.stats.topSpeed = 0;
    this.stats.maxDrop = 0;
    this.stats.airTime = 0;
    this.stats.portalTravels = 0;
  }
}
